import dayjs from 'dayjs';

import { CancellationType, UpcomingAppointment } from 'types';

import { formatPhoneNumbers } from './formats';

/** API / OLB flags often arrive as 0|1 numbers or "0"|"1" strings — not JS-boolean safe. */
export const isApiFlagEnabled = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;

  const normalized = String(value).trim().toLowerCase();
  if (normalized === '1' || normalized === 'true' || normalized === 'yes') {
    return true;
  }
  if (
    normalized === '0' ||
    normalized === 'false' ||
    normalized === 'no' ||
    normalized === ''
  ) {
    return false;
  }

  return Boolean(value);
};

export const isWithinLateCancelWindow = (
  appointment: Pick<
    UpcomingAppointment,
    'appt_start_dt' | 'appt_start_tm' | 'is_late_cancel'
  >,
  cancelLeadHours: number
): boolean => {
  if (!cancelLeadHours || cancelLeadHours <= 0) {
    return isApiFlagEnabled(appointment.is_late_cancel);
  }

  const { appt_start_dt, appt_start_tm } = appointment;
  if (!appt_start_dt || !appt_start_tm) {
    return isApiFlagEnabled(appointment.is_late_cancel);
  }

  const apptStart = dayjs(
    `${appt_start_dt} ${appt_start_tm}`,
    'YYYY-MM-DD HH:mm:ss'
  );

  if (!apptStart.isValid()) {
    return isApiFlagEnabled(appointment.is_late_cancel);
  }

  const hoursUntil = apptStart.diff(dayjs(), 'hour', true);
  return hoursUntil < cancelLeadHours;
};

export const getCancellationType = (
  isGlobalCancelAllowed: boolean,
  isLateCancel: boolean,
  isAppointmentCancelAllowed: boolean
): CancellationType => {
  if (!isGlobalCancelAllowed) {
    return CancellationType.DENIED;
  }

  if (isLateCancel && isAppointmentCancelAllowed) {
    return CancellationType.NOT_ALLOWED;
  }

  if (isAppointmentCancelAllowed) {
    return CancellationType.ALLOWED;
  }

  return CancellationType.DENIED;
};

export const getCancellationModalTitle = (type: CancellationType | null) => {
  switch (type) {
    case CancellationType.DENIED:
      return 'Cancellation Not Permitted';
    default:
      return 'Cancel Appointment';
  }
};

export const getCancellationModalSubtitle = (type: CancellationType | null) => {
  switch (type) {
    case CancellationType.ALLOWED:
      return 'Are you sure you’d like to cancel this appointment?';

    case CancellationType.NOT_ALLOWED:
      return 'You are subject to a cancellation fee if you continue';

    case CancellationType.DENIED:
      return 'Cancellations are not permitted within 24 hours';
    default:
      return '';
  }
};

export const getCancellationModalBodyParts = (
  type: CancellationType | null
): { prefix: string; suffix: string } => {
  switch (type) {
    case CancellationType.ALLOWED:
      return {
        prefix: 'To reschedule, please call ',
        suffix: ' or use our online scheduling system.'
      };

    case CancellationType.NOT_ALLOWED:
      return {
        prefix: 'To reschedule, please call us at ',
        suffix:
          ' or use our online booking system. Cancellations or changes made within 24 hours of your appointment may incur a cancellation fee.'
      };

    case CancellationType.DENIED:
      return {
        prefix: 'For assistance, please call us at ',
        suffix:
          ". We'll be happy to review your options. Please note that cancellations or missed appointments may be subject to a cancellation fee."
      };

    default:
      return { prefix: '', suffix: '' };
  }
};

export const getCancellationModalBodyText = (
  type: CancellationType | null,
  phone: string
) => {
  const { prefix, suffix } = getCancellationModalBodyParts(type);
  if (!prefix && !suffix) return '';
  return `${prefix}${formatPhoneNumbers(phone)}${suffix}`;
};
